import {
  AddressFormInputs,
  Button,
  Input,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import {
  type RequestBody,
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { OfficeIcon, UserIcon } from '@vrplatform/icons';
import { Drawer } from '@vrplatform/components-legacy';
import type { Maybe } from '@vrplatform/utils';
import { Box, Group, ScrollArea, Stack } from '@mantine/core';
import {
  OWNER_COMPANY_TYPES,
  type OwnerCompanyTypeValue,
  formatAddress,
  validateTin,
} from '@vrplatform/ui-common';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { validateEmail } from '../../utils';
import { DrawerCollapsableSection, DrawerHeader } from '../_components';

type Props = {
  opened: boolean;
  closeDrawer: () => void;
  vendor?: FormInputs;
  onVendorAdded?: (contactId: string) => void;
};

type FormInputs = {
  id: Maybe<string>;
  firstName: string;
  name: string;
  tenantId: string;
  email: string;
  phone: string;
  taxId: string;
  address: Parameters<typeof formatAddress>[0] | null | undefined;
  companyType: 'individual' | OwnerCompanyTypeValue;
};

export const VendorDrawer = ({
  closeDrawer,
  opened,
  vendor,
  onVendorAdded,
}: Props) => {
  const methods = useForm<FormInputs>({
    values: vendor,
  });

  const ownerType = methods.watch('companyType') || 'individual';
  const isIndividual = ownerType === 'individual';

  const { mutateAsync: insert } = useApiMutation('post', '/contacts');

  const { mutateAsync: update } = useApiMutation('put', '/contacts/{id}');

  const invalidate = useInvalidateApiQueries(['expenses', 'owners']);

  const submit = async (formData: FormInputs) => {
    try {
      let contactId: string;

      const dirty = methods.formState.dirtyFields;

      function checkDirty<TValue, TKey extends keyof typeof dirty>(
        key: TKey | TKey[],
        value: TValue
      ) {
        if (Array.isArray(key)) {
          return key.some((k) => dirty[k]) ? value : undefined;
        }

        if (dirty[key]) {
          return value;
        }

        return undefined;
      }

      const body: RequestBody<'put:/contacts/{id}'> = {
        email: checkDirty('email', formData.email?.trim() || undefined),
        phone: checkDirty('phone', formData.phone?.trim() || undefined),
        companyType: checkDirty(
          'companyType',
          formData.companyType === 'individual' ? null : formData.companyType
        ),
        firstName: checkDirty(
          ['companyType', 'firstName'],
          formData.companyType === 'individual'
            ? formData.firstName?.trim() || undefined
            : null
        ),
        name: checkDirty(
          ['companyType', 'name'],
          formData.name?.trim() || undefined
        ),
        taxIdentifier: checkDirty('taxId', formData.taxId?.trim() || undefined),
        address: checkDirty(
          'address',
          formData.address?.countryCode &&
            formData.address?.stateCode &&
            formData.address?.line1?.trim()
            ? {
                city: formData.address.city.trim(),
                line1: formData.address.line1.trim(),
                line2: formData.address.line2?.trim() || undefined,
                stateCode: formData.address.stateCode,
                countryCode: formData.address.countryCode,
                postalCode: formData.address.postcode.trim(),
                full: formatAddress(formData.address),
              }
            : null
        ),
      };

      if (vendor?.id) {
        const result = await update({
          params: {
            path: {
              id: vendor.id,
            },
          },
          body,
        });

        contactId = result.data.id;
      } else {
        const result = await insert({
          body: {
            type: 'vendor',
            ...body,
          },
        });

        contactId = result.data.id;
      }

      if (!contactId) {
        throw new Error(
          'There was an issue opening the vendor after success. Please reach out to support if the issue persists. '
        );
      }

      onVendorAdded?.(contactId);
      invalidate();
      closeDrawer();
    } catch (error) {
      showApiErrorNotification({
        error,
        title: "We couldn't add the vendor",
      });
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={closeDrawer}
      containerSx={{
        paddingInline: 0,
      }}
    >
      <DrawerHeader
        title={vendor ? 'Edit vendor' : 'Add vendor'}
        closeDrawer={closeDrawer}
        containerSx={(theme) => ({
          paddingBottom: 0,
          marginBottom: 0,
          paddingLeft: `${theme.spacing.sm}!important`,
        })}
      />
      <FormProvider {...methods}>
        <Box
          sx={() => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflowY: 'hidden',
            overflowX: 'unset',
          })}
        >
          <ScrollArea
            styles={(theme) => ({
              root: {
                flex: 1,
              },
              viewport: {
                paddingInline: theme.spacing.sm,
              },
            })}
          >
            <Stack mt="lg" gap="lg" pb="sm">
              <Stack pb="sm">
                <Controller
                  control={methods.control}
                  defaultValue="individual"
                  name="companyType"
                  rules={{
                    required: 'Type is required',
                  }}
                  render={({ field, fieldState: { error } }) => {
                    const individualOption = {
                      label: 'Individual',
                      value: 'individual',
                      icon: <UserIcon size={16} />,
                    };

                    const options = [
                      individualOption,
                      ...OWNER_COMPANY_TYPES.map((x) => ({
                        ...x,
                        icon: <OfficeIcon size={16} />,
                      })),
                    ];

                    return (
                      <InputWrapper
                        label="Type"
                        required
                        error={error?.message}
                      >
                        <InputSelect
                          data={{
                            options,
                            sort: null,
                          }}
                          type="single"
                          value={
                            options.find((x) => x.value === field.value) || null
                          }
                          inputProps={{
                            placeholder: 'Company type',
                            error: !!error,
                          }}
                          dropdownProps={{ width: 'target' }}
                          setValue={(e) => {
                            if (e?.value) {
                              if (
                                (e.value === 'individual' &&
                                  field.value !== 'individual') ||
                                (e.value !== 'individual' &&
                                  field.value === 'individual')
                              ) {
                                methods.setValue('firstName', '');
                                methods.setValue('name', '');
                                methods.clearErrors('firstName');
                                methods.clearErrors('name');
                              }
                              field.onChange(e.value);
                            }
                          }}
                        />
                      </InputWrapper>
                    );
                  }}
                />

                {!isIndividual ? (
                  <Controller
                    control={methods.control}
                    name="name"
                    defaultValue=""
                    rules={{
                      required: 'Company name is required',
                      validate: trapSpacesForRequiredFields,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <InputWrapper
                        label="Company name"
                        error={error?.message}
                        required
                      >
                        <Input
                          {...field}
                          placeholder="Company name"
                          autoComplete="off"
                          error={!!error}
                        />
                      </InputWrapper>
                    )}
                  />
                ) : (
                  <Box
                    sx={(theme) => ({
                      alignItems: 'flex-start',
                      justifyContent: 'stretch',
                      display: 'flex',
                      gap: theme.spacing.md,
                      '> div': {
                        flex: 1,
                      },
                    })}
                  >
                    <Controller
                      control={methods.control}
                      name="firstName"
                      defaultValue=""
                      rules={{
                        required: 'First name is required',
                        validate: trapSpacesForRequiredFields,
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <InputWrapper
                          label="First name"
                          error={error?.message}
                          required={true}
                        >
                          <Input
                            {...field}
                            placeholder="First name"
                            autoComplete="off"
                            error={!!error}
                          />
                        </InputWrapper>
                      )}
                    />
                    <Controller
                      control={methods.control}
                      name="name"
                      defaultValue=""
                      rules={{
                        required: 'Last name is required',
                        validate: trapSpacesForRequiredFields,
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <InputWrapper
                          label="Last name"
                          error={error?.message}
                          required={true}
                        >
                          <Input
                            {...field}
                            placeholder="Last name"
                            autoComplete="off"
                            error={!!error}
                          />
                        </InputWrapper>
                      )}
                    />
                  </Box>
                )}

                <Controller
                  control={methods.control}
                  name="email"
                  rules={{
                    validate: (value) => validateEmail(value),
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <InputWrapper label="Email" error={error?.message}>
                      <Input
                        {...field}
                        placeholder="Email"
                        autoComplete="off"
                        type="email"
                        error={!!error}
                      />
                    </InputWrapper>
                  )}
                />

                <Controller
                  control={methods.control}
                  name="phone"
                  render={({ field, fieldState: { error } }) => (
                    <InputWrapper label="Phone" error={error?.message}>
                      <Input
                        {...field}
                        ref={undefined}
                        placeholder="Phone"
                        type="tel"
                        autoComplete="off"
                        error={!!error}
                      />
                    </InputWrapper>
                  )}
                />
              </Stack>

              <DrawerCollapsableSection title="Address">
                <Box pb="sm">
                  <AddressFormInputs disableType="country-select" />
                </Box>
              </DrawerCollapsableSection>

              <DrawerCollapsableSection title="Tax information">
                <Controller
                  control={methods.control}
                  name="taxId"
                  rules={{
                    validate: (value, formValues) => {
                      const isUSA =
                        formValues.address?.countryCode?.toLowerCase() === 'us';

                      if (value?.trim() && isUSA) {
                        const isError = typeof validateTin(value) === 'string';

                        return isError
                          ? 'Please enter a valid 9-digit US TIN or leave the input blank'
                          : true;
                      }

                      return true;
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <InputWrapper label="TIN " error={error?.message} mb="sm">
                      <Input
                        {...field}
                        placeholder="Tax Identification Number"
                        autoComplete="off"
                        error={!!error}
                      />
                    </InputWrapper>
                  )}
                />
              </DrawerCollapsableSection>
            </Stack>
          </ScrollArea>

          <Box
            sx={(theme) => ({
              paddingBlock: theme.spacing.sm,
              paddingInline: theme.spacing.sm,
              borderTop: `1px solid ${theme.colors.gray[2]}`,
            })}
          >
            <Group>
              <Button
                onClick={() => closeDrawer()}
                disabled={methods.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                sx={{ flexGrow: '1!important' as any }}
                type="submit"
                disabled={!methods.formState.isDirty}
                onClick={methods.handleSubmit(submit)}
                variant="primary"
                loading={methods.formState.isSubmitting}
              >
                Submit
              </Button>
            </Group>
          </Box>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';
